import { z } from 'zod';
import { AutocompleteSuggestion, GeocodeResult } from './mapbox.js';
import { ParcelRow } from './parcel.js';
import { PermitRow } from './permit.js';
// --- shared error shape ----------------------------------------------------
export const AppError = z.object({
    error: z.string(),
    code: z.string(),
    details: z.unknown().optional(),
});
// --- auth ------------------------------------------------------------------
// Cap the password length so a client can't ship 10 MB of bytes and force
// the server to allocate two equally-sized Buffers for the timing-safe
// compare. 256 chars is well beyond any reasonable human password.
// --- mapbox proxies --------------------------------------------------------
export const AutocompleteQuery = z.object({
    q: z.string().min(1),
    /** Optional Mapbox proximity bias as "lng,lat". */
    proximity: z
        .string()
        .regex(/^-?\d+(\.\d+)?,-?\d+(\.\d+)?$/)
        .optional(),
});
export const AutocompleteResponse = z.object({
    suggestions: z.array(AutocompleteSuggestion),
});
export const GeocodeRetrieveQuery = z.object({
    /** Mapbox suggestion id from /api/autocomplete (preferred) ... */
    mapbox_id: z.string().optional(),
    /** ... or a freeform string (forward geocoding fallback). */
    q: z.string().optional(),
});
export const GeocodeResponse = GeocodeResult;
// --- parcels ---------------------------------------------------------------
const Bbox = z.string().transform((s, ctx) => {
    const parts = s.split(',').map((p) => Number(p.trim()));
    if (parts.length !== 4 || parts.some(Number.isNaN)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'expected 4 numeric csv values' });
        return z.NEVER;
    }
    const [w, s2, e, n] = parts;
    if (w >= e) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'west must be < east' });
        return z.NEVER;
    }
    if (s2 >= n) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'south must be < north' });
        return z.NEVER;
    }
    return [w, s2, e, n];
});
export const ParcelsQuery = z.object({ bbox: Bbox });
export const ParcelsResponse = z.object({
    parcels: z.array(ParcelRow),
});
// --- permits at address ----------------------------------------------------
const numStr = z.string().transform((s, ctx) => {
    const n = Number(s);
    if (Number.isNaN(n)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'not a number' });
        return z.NEVER;
    }
    return n;
});
export const PermitsQuery = z.object({
    lat: numStr,
    lng: numStr,
    line1: z.string().optional(),
    // Locality of the searched address — used to pin the line1 match so an
    // identical street line in another city (e.g. "4310 Avenue H" in both
    // Austin 78751 and Rosenberg 77471) doesn't bleed in.
    city: z.string().optional(),
    state: z.string().optional(),
    postal_code: z.string().optional(),
    apn: z.string().optional(),
});
export const PermitsResponse = z.object({
    permits: z.array(PermitRow),
});
// --- counts ----------------------------------------------------------------
const State2 = z.string().regex(/^[A-Z]{2}$/);
export const CountsQuery = z.object({
    street: z.string().min(1),
    city: z.string().min(1),
    state: State2,
    postal_code: z.string().min(1),
});
export const TagCount = z.object({ tag: z.string(), count: z.number().int().nonnegative() });
const CountsCell = z.object({
    unique_addresses: z.number().int().nonnegative(),
    unique_permits: z.number().int().nonnegative(),
    /** The scope's most common tags, busiest first (#171 Counts box). */
    top_tags: z.array(TagCount).default([]),
});
export const CountsResponse = z.object({
    street: CountsCell,
    city: CountsCell,
    zip: CountsCell,
});
/** Permit-number / APN search (#171). */
export const SearchPermitsQuery = z.object({
    kind: z.enum(['permit', 'apn']),
    q: z.string().trim().min(1).max(100),
    state: State2.optional(),
});
// --- addresses drilldown ---------------------------------------------------
export const AddressesQuery = z
    .object({
    scope: z.enum(['street', 'city', 'zip']),
    street: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    postal_code: z.string().optional(),
})
    .superRefine((v, ctx) => {
    if (v.scope === 'street' && (!v.street || !v.city || !v.state || !v.postal_code)) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'street scope needs street, city, state, postal_code',
        });
    }
    if (v.scope === 'city' && (!v.city || !v.state)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'city scope needs city, state' });
    }
    if (v.scope === 'zip' && !v.postal_code) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'zip scope needs postal_code' });
    }
});
export const AddressLite = z.object({
    address_line1: z.string(),
    city: z.string().nullable(),
    state: z.string().nullable(),
    postal_code: z.string().nullable(),
});
export const AddressesResponse = z.object({
    addresses: z.array(AddressLite),
    total: z.number().int().nonnegative(),
    capped_at: z.number().int().positive(),
});
//# sourceMappingURL=api.js.map